import { formatWhen, toDate } from '../../../../../utils/sms';
import { StatusTone } from '../../../../components/inputs/status-filter/status-filter.component';

/**
 * Plain helpers shared by the campaign list and the create wizard: how the
 * backend's campaign states group for people, and how a schedule's date,
 * time and time zone become a real moment.
 */

/** A set of backend `Status` values that read as one state to an operator. */
export interface CampaignStatusGroup {
  /** Filter value and URL value (`?status=running`). */
  key: string;
  /** Filter label. */
  title: string;
  /** Word used after a count in the header: "3 running", "4 drafts". */
  noun: (count: number) => string;
  /** Backend enum names (EngagementCampaign.status), sent as `status.in`. */
  statuses: string[];
  icon: string;
  tone: StatusTone;
  /** Dot colour token in the header counts. */
  dot: 'ok' | 'wait' | 'busy' | 'bad' | 'idle';
}

/**
 * Every value of the backend Status enum, grouped. The API filters on the
 * enum names, so these stay upper case even though rows are compared in
 * lower case.
 */
export const CAMPAIGN_STATUS_GROUPS: CampaignStatusGroup[] = [
  {
    key: 'running',
    title: 'Running',
    noun: () => 'running',
    statuses: ['IN_PROGRESS', 'ACTIVE', 'RESUMING', 'PAUSING'],
    icon: 'play_circle',
    tone: 'info',
    dot: 'busy',
  },
  {
    key: 'scheduled',
    title: 'Scheduled',
    noun: () => 'scheduled',
    statuses: ['SCHEDULED'],
    icon: 'schedule',
    tone: 'warning',
    dot: 'wait',
  },
  {
    key: 'paused',
    title: 'Paused',
    noun: () => 'paused',
    statuses: ['PAUSED'],
    icon: 'pause_circle',
    tone: 'warning',
    dot: 'wait',
  },
  {
    key: 'completed',
    title: 'Completed',
    noun: () => 'completed',
    statuses: ['COMPLETED', 'FINISHED'],
    icon: 'check_circle',
    tone: 'success',
    dot: 'ok',
  },
  {
    key: 'failed',
    title: 'Failed',
    noun: () => 'failed',
    statuses: ['FAILED'],
    icon: 'error',
    tone: 'danger',
    dot: 'bad',
  },
  {
    key: 'stopped',
    title: 'Stopped',
    noun: () => 'stopped',
    statuses: ['CANCELED', 'DISABLED'],
    icon: 'block',
    tone: 'neutral',
    dot: 'idle',
  },
  {
    key: 'draft',
    title: 'Draft',
    noun: (count) => (count === 1 ? 'draft' : 'drafts'),
    statuses: ['DRAFTED'],
    icon: 'edit_note',
    tone: 'neutral',
    dot: 'idle',
  },
];

export function statusGroupOf(status: string | null | undefined): CampaignStatusGroup | null {
  const value = String(status ?? '').trim().toUpperCase();
  if (!value) return null;
  return CAMPAIGN_STATUS_GROUPS.find((group) => group.statuses.includes(value)) ?? null;
}

/** Campaigns that may still send: their next send is worth looking up. */
export function canStillSend(status: string | null | undefined): boolean {
  const key = statusGroupOf(status)?.key;
  return key === 'running' || key === 'scheduled' || key === 'paused';
}

/** Campaigns that have not sent anything yet, so there are no receipts. */
export function hasNotSent(status: string | null | undefined): boolean {
  const key = statusGroupOf(status)?.key;
  return key === 'scheduled' || key === 'draft';
}

/** Running campaigns are still collecting delivery receipts. */
export function isRunning(status: string | null | undefined): boolean {
  return statusGroupOf(status)?.key === 'running';
}

/** The browser's IANA zone, e.g. "Africa/Gaborone". Safe during SSR. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const zoneFormats = new Map<string, Intl.DateTimeFormat>();

/** Milliseconds a zone is ahead of UTC at a given instant, or null for an unknown zone. */
function zoneOffset(instant: number, timeZone: string): number | null {
  try {
    let format = zoneFormats.get(timeZone);
    if (!format) {
      format = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      zoneFormats.set(timeZone, format);
    }
    const parts = format.formatToParts(new Date(instant));
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return asUtc - instant;
  } catch {
    return null;
  }
}

/**
 * A schedule's date ("2026-10-14"), time ("09:30") and zone
 * ("Africa/Gaborone") as the instant it fires. Without a zone, or with one
 * the browser does not know, the wall time is read in the browser's zone.
 */
export function scheduleMoment(
  date: string | null | undefined,
  time: string | null | undefined,
  timeZone?: string | null
): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date ?? ''));
  if (!day) return toDate(date ?? null);
  const clock = /^(\d{1,2}):(\d{2})/.exec(String(time ?? '')) ?? [null, '0', '0'];
  const [y, m, d, h, min] = [Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(clock[1]), Number(clock[2])];

  if (timeZone) {
    const wall = Date.UTC(y, m, d, h, min);
    const first = zoneOffset(wall, timeZone);
    if (first !== null) {
      let instant = wall - first;
      // Near a daylight-saving change the offset at the answer can differ.
      const second = zoneOffset(instant, timeZone);
      if (second !== null && second !== first) instant = wall - second;
      return new Date(instant);
    }
  }
  return new Date(y, m, d, h, min);
}

/**
 * "Tue 13 Oct, 09:00", read on the wall clock the schedule was written
 * for, plus the zone when it is not the browser's.
 */
export function formatScheduleWhen(
  date: string | null | undefined,
  time: string | null | undefined,
  timeZone?: string | null
): string {
  const wall = scheduleMoment(date, time, null);
  if (!wall) return '—';
  const label = formatWhen(wall);
  return timeZone && timeZone !== browserTimeZone() ? `${label} ${zoneLabel(timeZone)}` : label;
}

/** "Africa/Gaborone" → "Gaborone time". */
export function zoneLabel(timeZone: string | null | undefined): string {
  if (!timeZone) return '';
  if (/^(UTC|GMT)$/i.test(timeZone)) return timeZone.toUpperCase();
  const city = timeZone.split('/').pop() ?? timeZone;
  return `${city.replace(/_/g, ' ')} time`;
}

/** A date-only string ("2026-10-12") as a local day; anything else as a date. */
export function toDay(value: string | null | undefined): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''));
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  return toDate(value ?? null);
}

const dayMonth = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dayMonthYear = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** "12 Oct – 30 Nov 2026", or "12 Oct 2026" for a one-day run. */
export function formatRun(start: string | null | undefined, end: string | null | undefined): string {
  const from = toDay(start);
  const to = toDay(end);
  if (!from && !to) return '—';
  if (!from || !to) return dayMonthYear.format((from ?? to) as Date);
  if (from.toDateString() === to.toDateString()) return dayMonthYear.format(from);
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${sameYear ? dayMonth.format(from) : dayMonthYear.format(from)} – ${dayMonthYear.format(to)}`;
}

/** "49 days", "1 day", or null when the dates are missing. */
export function formatRunLength(start: string | null | undefined, end: string | null | undefined): string | null {
  const from = toDay(start);
  const to = toDay(end);
  if (!from || !to) return null;
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  if (days <= 1) return '1 day';
  return `${days} days`;
}

/** "1 part" / "3 parts". */
export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}
